import { useState } from "react";

import { refusalText } from "../../../api/errors";
import { useApiQuery } from "../../../api/hooks";
import { Button } from "../../../ui/Button";
import { Card } from "../../../ui/Card";
import { EmptyState, Loading } from "../../../ui/EmptyState";
import { plural } from "../shared/format";
import { AddAccountDialog } from "./AddAccountDialog";
import { PersonRow } from "./PersonRow";

/** How often the list is read again: the admin has no live channel, so "active now" is kept fresh this way. */
export const PEOPLE_REFRESH_MS = 30_000;

/** Accounts with roles, disable and enable, first passwords, who is active now and last sign-in (ui-direction screen 10). */
export function AccountsSection({ pointed }: { pointed: boolean }) {
  const people = useApiQuery("admin_people", {}, { refetchInterval: PEOPLE_REFRESH_MS });
  const workspaces = useApiQuery("admin_workspaces", {});
  const [adding, setAdding] = useState(false);
  const list = people.data ?? [];

  return (
    <Card className="ra-section" id="accounts" aria-labelledby="ra-accounts-title" data-pointed={pointed ? "true" : undefined}>
      <div className="ra-section__head row">
        <h2 id="ra-accounts-title">Accounts</h2>
        {people.data && <span className="muted">{plural(list.length, "account")}</span>}
        <Button variant="primary" onClick={() => setAdding(true)}>
          Add an account
        </Button>
      </div>
      <p className="muted">
        Roles are held in a discipline. A person may hold both roles; their own work is then marked "your own work" in the SME inbox. Disabling an account ends its sessions and releases its locks, and never deletes its work.
      </p>
      {people.isPending && <Loading>Reading the accounts</Loading>}
      {people.isError && (
        <p role="alert" className="form-error">
          {refusalText(people.error)} <Button onClick={() => void people.refetch()}>Try again</Button>
        </p>
      )}
      {people.data && list.length === 0 && (
        <EmptyState title="No account yet" action={<Button onClick={() => setAdding(true)}>Add the first account</Button>}>
          Add an account for each engineer and SME, then add them to a discipline.
        </EmptyState>
      )}
      {list.length > 0 && (
        <ul className="ra-people" aria-label="Accounts">
          {list.map((person) => (
            <PersonRow key={person.id} person={person} workspaces={workspaces.data ?? []} />
          ))}
        </ul>
      )}
      {adding && <AddAccountDialog existing={list} onClose={() => setAdding(false)} />}
    </Card>
  );
}
